import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { CheckCircle2, Clock, ChevronRight } from "@/components/icons";
import StatCard from "../../components/StatCard";
import {
  formatWeekRange,
  groupSalesByWeek,
  weekStart,
} from "../../lib/weekCutoff";

export default function PharmacySalesHistory() {
  const [sales, setSales] = useState([]);
  const [expanded, setExpanded] = useState(null); // key = week start ts

  useEffect(() => {
    api.get("/pharmacy/sales").then((r) => setSales(r.data));
  }, []);

  const weeks = useMemo(() => groupSalesByWeek(sales), [sales]);
  const now = new Date();
  const currentWeekTs = weekStart(now).getTime();

  const totalPending = weeks.reduce((a, w) => a + w.pending, 0);
  const totalSold = weeks.reduce((a, w) => a + w.tot, 0);

  return (
    <div className="space-y-6" data-testid="pharmacy-sales-history">
      <div>
        <h1 className="text-3xl font-extrabold text-[#163144] tracking-tight">
          Historial semanal
        </h1>
        <p className="text-sm text-[#1B405B]/70">
          Cortes por semana operativa (viernes → jueves). Cada fila resume la
          venta total, comisión de la plataforma y estado del pago. Vuelve a{" "}
          <Link
            to="/farmacia/ventas"
            className="font-bold underline text-[#01BC8E]"
          >
            Ventas y comisión
          </Link>{" "}
          para la semana en curso.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 pt-2">
        <StatCard
          title="Total histórico vendido"
          value={`$${totalSold.toFixed(2)}`}
          subtitle={`${sales.length} venta${sales.length === 1 ? "" : "s"}`}
          testid="hstat-sold"
        />
        <StatCard
          title="Total atrasado"
          value={`$${totalPending.toFixed(2)}`}
          subtitle="Sin pagar de semanas anteriores"
          active={totalPending > 0}
          testid="hstat-overdue"
        />
      </div>

      <div className="bg-white rounded-[28px] overflow-x-auto border border-black/[0.06]">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="text-left">
            <tr className="text-[11px] uppercase tracking-widest text-[#1B405B]/70 border-b border-[#1B405B]/10">
              <th className="px-5 py-4">Semana</th>
              <th className="px-5 py-4 text-right">Ventas</th>
              <th className="px-5 py-4 text-right">Total</th>
              <th className="px-5 py-4 text-right">Comisión</th>
              <th className="px-5 py-4">Estado</th>
              <th className="px-5 py-4" />
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => {
              const key = w.start.getTime();
              const isCurrent = key === currentWeekTs;
              const isOverdue = !isCurrent && !w.allPaid;
              const isOpen = expanded === key;
              return (
                <>
                  <tr
                    key={key}
                    className={`border-b border-[#1B405B]/8 cursor-pointer hover:bg-black/[0.02] ${
                      isOpen ? "bg-black/[0.02]" : ""
                    }`}
                    onClick={() => setExpanded(isOpen ? null : key)}
                    data-testid={`week-row-${key}`}
                  >
                    <td className="px-5 py-3 font-bold text-[#163144]">
                      {formatWeekRange(w.start)}
                      {isCurrent && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-[#01BC8E] bg-blue-50 px-2 py-0.5 rounded-full">
                          En curso
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">{w.sales.length}</td>
                    <td className="px-5 py-3 text-right">
                      ${w.tot.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-right font-bold text-[#1B405B]">
                      ${w.com.toFixed(2)}
                    </td>
                    <td className="px-5 py-3">
                      {isCurrent ? (
                        <span className="inline-flex items-center gap-1 text-[#1B405B] text-xs font-bold">
                          <Clock className="w-4 h-4" /> En curso
                        </span>
                      ) : w.allPaid ? (
                        <span className="inline-flex items-center gap-1 text-[#019A75] text-xs font-bold">
                          <CheckCircle2 className="w-4 h-4" /> Pagado
                        </span>
                      ) : isOverdue ? (
                        <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-bold">
                          <Clock className="w-4 h-4" /> Atrasado (${w.pending.toFixed(2)})
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-bold">
                          <Clock className="w-4 h-4" /> Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <ChevronRight
                        className={`w-4 h-4 text-black/40 transition ${
                          isOpen ? "rotate-90" : ""
                        }`}
                      />
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${key}-expanded`}>
                      <td colSpan={6} className="bg-[#F5F5F5] px-5 py-4">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-widest text-black/50">
                              <th className="pb-2 text-left">Fecha</th>
                              <th className="pb-2 text-left">Receta</th>
                              <th className="pb-2 text-right">Venta</th>
                              <th className="pb-2 text-right">Comisión</th>
                              <th className="pb-2">Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {w.sales.map((s) => (
                              <tr key={s.id} className="border-t border-black/5">
                                <td className="py-2">
                                  {new Date(s.created_at).toLocaleDateString(
                                    "es-ES",
                                    {
                                      weekday: "short",
                                      day: "numeric",
                                      month: "short",
                                    },
                                  )}
                                </td>
                                <td className="py-2 font-bold text-[#163144]">
                                  {s.prescription_id.slice(0, 8).toUpperCase()}
                                </td>
                                <td className="py-2 text-right">
                                  ${s.total_amount.toFixed(2)}
                                </td>
                                <td className="py-2 text-right text-[#1B405B]">
                                  ${s.platform_commission_amount.toFixed(2)}
                                </td>
                                <td className="py-2">
                                  {s.pharmacy_paid ? (
                                    <span className="text-[#019A75] font-bold">
                                      Pagada
                                    </span>
                                  ) : (
                                    <span className="text-amber-700 font-bold">
                                      Pendiente
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {weeks.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-[#1B405B]/60 py-10">
                  No hay historial de ventas todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
